import {
  TextField,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Typography,
  Chip,
} from "@mui/material";

import {
  Circle as CircleIcon,
  RadioButtonUnchecked as OfflineIcon,
  Block as UnavailableIcon,
} from "@mui/icons-material";
import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../../api";
import { getSession } from "../../../auth";

const STATUS_PRIORITY = {
  online: 0,
  busy: 1,
  offline: 2,
  unavailable: 3,
};

const getStatusPriority = (status) => {
  if (!status) return 99;
  return STATUS_PRIORITY[status.toLowerCase()] ?? 99;
};

// Ordem de grupos no dropdown:
// 0 - Ramais online
// 1 - Contatos do CRM
// 2 - Demais Ramais
// 3 - Filas
// 4 - Operadores
const getGroupPriority = (contact) => {
  const type = contact.type;
  const status = contact.status?.toLowerCase();

  if (type === "Ramal" && status === "online") {
    return 0; // Ramais online
  }

  if (type === "Contato") {
    return 1; // Contatos do CRM
  }

  if (type === "Ramal") {
    return 2; // Demais Ramais
  }

  if (type === "Fila") {
    return 3; // Filas
  }

  if (type === "Operador") {
    return 4; // Operadores
  }

  return 99;
};

const sortContacts = (contacts) => {
  return [...contacts].sort((a, b) => {
    const gA = getGroupPriority(a);
    const gB = getGroupPriority(b);

    if (gA !== gB) return gA - gB;

    const sA = getStatusPriority(a.status);
    const sB = getStatusPriority(b.status);

    if (sA !== sB) return sA - sB;

    // desempate por nome
    return (a.name || "").localeCompare(b.name || "");
  });
};

const ContactsAutocomplete = ({
  searchValue,
  setSearchValue,
  handleEnterAction,
  includeCRM = true,
  onContactSelect,
  onAfterSelect,
}) => {
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const searchTimeoutRef = useRef(null);
  const lastRequestIdRef = useRef(0);
  //const popupToggleRef = useRef(null); // controla fechar/reabrir o popup
  const prevResultsLengthRef = useRef(0); // para saber se a lista ficou menor

  // Handle contact selection from dropdown
  const handleContactSelect = (event, selectedContact) => {
    if (selectedContact && selectedContact.number) {
      // If onContactSelect callback is provided, use it (for multi-select scenarios like conference)
      if (onContactSelect) {
        onContactSelect(event, selectedContact);
        setSearchResults([]);
        setAutocompleteOpen(false);
        return;
      }

      // Default behavior: extract and set the number value
      let number;
      if (selectedContact.number.includes('_')) {
        number = selectedContact.number.split('_')[1];
      } else {
        number = selectedContact.number;
      }
      number = number.replace(/\D/g, ''); // Remove non-numeric characters

      setSearchValue(number);
      setSearchResults([]);
      setAutocompleteOpen(false);
      requestAnimationFrame(() => onAfterSelect?.());
    }
  };

  // Handle autocomplete close
  const handleAutocompleteClose = () => {
    setAutocompleteOpen(false);
  };

  const performSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) {
      setSearchResults([]);
      setAutocompleteOpen(false);
      prevResultsLengthRef.current = 0;
      return;
    }

    // id desta requisição
    const requestId = ++lastRequestIdRef.current;
    console.log("Searching for:", searchTerm, "include ? ", includeCRM);

    try {
      const enterpriseId = getSession().profile.enterprise.id;
      const agentId = getSession().profile.agent_id;

      const searchParam = searchTerm
        ? `search=${encodeURIComponent(searchTerm)}`
        : "";

      const contacts = [];
      let ivResponse, crmResponse = null;

      if (includeCRM) {
        [crmResponse, ivResponse] = await Promise.all([
            api.get(`api/enterprises/${enterpriseId}/contacts/?${searchParam}`),
            api.get(
            `/api/enterprises/${enterpriseId}/agents/${agentId}/iv_contacts/?${searchParam}`,
            { timeout: 15000 }
            ),
        ]);

        // Handle CRM contacts - check if it's paginated or direct array
        if (crmResponse.status >= 200 && crmResponse.status < 300) {
            const crmData = crmResponse.data.results || crmResponse.data || [];
            console.log("CRM response:", crmData);
            contacts.push(
            ...crmData.map((contact) => ({
                id: `crm-${contact.id}`,
                name: contact.name || contact.telephone,
                number: contact.telephone || "",
                type: "Contato",
                status: "Unavailable",
            }))
            );
        }
      } else {
        ivResponse = await api.get(
          `/api/enterprises/${enterpriseId}/agents/${agentId}/iv_contacts/?${searchParam}`,
          { timeout: 15000 }
        );
      }

      // Handle IV contacts
      if (ivResponse.status >= 200 && ivResponse.status < 300) {
        if (
          ivResponse.data.agent_contacts &&
          ivResponse.data.agent_contacts.length > 0
        ) {
          contacts.push(
            ...ivResponse.data.agent_contacts.map((contact) => ({
              id: `agent-${contact.id}`,
              name: contact.name || contact.user,
              number: contact.user,
              type: "Operador",
              status: contact.status,
            }))
          );
        }

        if (
          ivResponse.data.extension_contacts &&
          ivResponse.data.extension_contacts.length > 0
        ) {
          contacts.push(
            ...ivResponse.data.extension_contacts.map((contact) => ({
              id: `ext-${contact.id}`,
              name: contact.description || contact.username,
              number: contact.username,
              type: "Ramal",
              status: contact.status,
            }))
          );
        }

        if (
          ivResponse.data.queue_contacts &&
          ivResponse.data.queue_contacts.length > 0
        ) {
          contacts.push(
            ...ivResponse.data.queue_contacts.map((contact) => ({
              id: `queue-${contact.id}`,
              name: contact.description || contact.name,
              number: contact.name,
              type: "Fila",
              status: contact.status,
            }))
          );
        }
      }

      const orderedContacts = sortContacts(contacts);

      // só aplica se essa ainda for a requisição mais recente
      if (requestId !== lastRequestIdRef.current) {
        return;
      }

      setSearchResults(orderedContacts);

      const prevLen = prevResultsLengthRef.current;
      const newLen = orderedContacts.length;
      prevResultsLengthRef.current = newLen;
      
      // Se lista ficou menor, faz um toggle leve no popup;
      // se aumentou ou manteve, só ajusta normalmente.
      if (prevLen > newLen) {
        setAutocompleteOpen(false);
        setAutocompleteOpen(newLen > 0);
      } else {
        setAutocompleteOpen(newLen > 0);
      }
    } catch (error) {
      console.error("Search error:", error);
      if (requestId === lastRequestIdRef.current) {
        setSearchResults([]);
        setAutocompleteOpen(false);
      }
    }
    },
    [includeCRM]
  );

  // Handle search input change with 1 second delay
  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 500);
    },
    [performSearch]
  );

  // Helper function to get status display properties
  const getStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case "online":
        return {
          icon: <CircleIcon sx={{ fontSize: 12 }} />,
          color: "#4caf50", // Green
          label: "Online",
          bgColor: "#e8f5e8",
        };
      case "busy":
        return {
          icon: <CircleIcon sx={{ fontSize: 12 }} />,
          color: "#ff9800", // Orange
          label: "Ocupado",
          bgColor: "#fff3e0",
        };
      case "offline":
        return {
          icon: <OfflineIcon sx={{ fontSize: 12 }} />,
          color: "#f44336", // Red
          label: "Offline",
          bgColor: "#ffebee",
        };
      case "unavailable":
      default:
        return {
          icon: <UnavailableIcon sx={{ fontSize: 12 }} />,
          color: "#9e9e9e", // Gray
          label: "Indisponível",
          bgColor: "#f5f5f5",
        };
    }
  };

  const cancelSearchAndClose = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    lastRequestIdRef.current += 1;

    setAutocompleteOpen(false);
    setSearchResults([]);
    prevResultsLengthRef.current = 0;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Autocomplete
      options={searchResults}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return option.name;
      }}
      renderOption={(props, option) => {
        const statusDisplay = getStatusDisplay(option.status);

        return (
          <Box component="li" {...props}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.number} • {option.type}
                </Typography>
              </Box>
              <Chip
                icon={statusDisplay.icon}
                label={statusDisplay.label}
                size="small"
                sx={{
                  backgroundColor: statusDisplay.bgColor,
                  color: statusDisplay.color,
                  border: `1px solid ${statusDisplay.color}`,
                  "& .MuiChip-icon": {
                    color: statusDisplay.color,
                  },
                }}
              />
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          placeholder="Digite o número ou nome do contato"
          inputProps={{
            ...params.inputProps,
            onKeyDownCapture: (e) => {
              // avoid IME/composition issues
              if (e.isComposing || e.nativeEvent?.isComposing) return;

              if (e.key === "Enter") {
                // handle typed quickly and state not updated yet
                const typed = typeof e.currentTarget?.value === "string"
                  ? e.currentTarget.value
                  : (searchValue || "");

                e.preventDefault();
                e.stopPropagation();
                if (e.nativeEvent?.stopImmediatePropagation) {
                  e.nativeEvent.stopImmediatePropagation();
                }
                cancelSearchAndClose();
                handleEnterAction(typed);
              }
            },
          }}
        />
      )}
      inputValue={searchValue ? searchValue : ""}
      onInputChange={(event, value, reason) => {
        if (reason === "input") {
          handleSearchChange(value);
        } else if (reason === "clear") {
          setSearchValue("");
          setSearchResults([]);
          setAutocompleteOpen(false);
          prevResultsLengthRef.current = 0;
        }
      }}
      onChange={handleContactSelect}
      onClose={handleAutocompleteClose}
      freeSolo
      clearOnBlur={false}
      blurOnSelect={true}
      open={autocompleteOpen && searchResults && searchResults.length > 0}
      filterOptions={(options) => options} // Disable client-side filtering
    />
  );
};

export default ContactsAutocomplete;
